import React from 'react';
import { Paragraph } from '@arctheme-components/elements/Paragraph/Paragraph';
import useCurrentTextStyle from '../../../../hooks/useCurrentTextStyle';

interface MinicartUSPProps {
  block?: any;
  settings: {
    // Content settings
    show_usp?: boolean;
    money_back_text?: string;
    money_back_text_style?: string;
    money_back_text_style_enable_responsive?: boolean;
    money_back_text_style_mobile?: string;
    money_back_text_style_tablet?: string;
    money_back_text_style_ultrawide?: string;
    money_back_text_color?: string;
    free_shipping_text?: string;
    free_shipping_text_style?: string;
    free_shipping_text_style_enable_responsive?: boolean;
    free_shipping_text_style_mobile?: string;
    free_shipping_text_style_tablet?: string;
    free_shipping_text_style_ultrawide?: string;
    free_shipping_text_color?: string;
    usp_text_style?: string;
    usp_text_style_enable_responsive?: boolean;
    usp_text_style_mobile?: string;
    usp_text_style_tablet?: string;
    usp_text_style_ultrawide?: string;
    usp_text_color?: string;

    // Appearance settings
    color_schema?: string;
    border_radius?: number;

    // Layout & Spacing settings
    direction?: string;
    padding_top?: number;
    padding_bottom?: number;
    padding_left_right?: number;
    margin_top?: number;
    margin_bottom?: number;
    margin_left_right?: number;

    // Divider settings
    show_divider?: boolean;
    divider_color?: string;
    divider_thickness?: number;
  };
}

export const MinicartUSP = ({ settings, block }: MinicartUSPProps) => {
  // Early return if USP is disabled
  if (settings?.show_usp === false) {
    return null;
  }

  const { getCurrentTextStyle } = useCurrentTextStyle();

  // Text styling with responsive support
  const defaultTextStyle = getCurrentTextStyle(
    settings?.usp_text_style,
    settings?.usp_text_style_enable_responsive,
    settings?.usp_text_style_mobile,
    settings?.usp_text_style_tablet,
    settings?.usp_text_style_ultrawide
  );
  const defaultTextColor = settings?.usp_text_color;
  const moneyBackTextStyle = getCurrentTextStyle(
    settings?.money_back_text_style || defaultTextStyle,
    settings?.money_back_text_style_enable_responsive,
    settings?.money_back_text_style_mobile,
    settings?.money_back_text_style_tablet,
    settings?.money_back_text_style_ultrawide
  );
  const moneyBackTextColor = settings?.money_back_text_color || defaultTextColor;
  const freeShippingTextStyle = getCurrentTextStyle(
    settings?.free_shipping_text_style || defaultTextStyle,
    settings?.free_shipping_text_style_enable_responsive,
    settings?.free_shipping_text_style_mobile,
    settings?.free_shipping_text_style_tablet,
    settings?.free_shipping_text_style_ultrawide
  );
  const freeShippingTextColor = settings?.free_shipping_text_color || defaultTextColor;

  // Layout & Spacing
  const direction = settings?.direction || 'column';
  const paddingTop = settings?.padding_top ?? 16;
  const paddingBottom = settings?.padding_bottom ?? 16;
  const paddingLeftRight = settings?.padding_left_right ?? 16;
  const marginTop = settings?.margin_top ?? 0;
  const marginBottom = settings?.margin_bottom ?? 20;
  const marginLeftRight = settings?.margin_left_right ?? 0;

  // Appearance
  const borderRadius = settings?.border_radius || 0;

  // Divider settings
  const showDivider = settings?.show_divider || false;
  const dividerColor = settings?.divider_color || '#e5e7eb';
  const dividerThickness = settings?.divider_thickness || 1;

  const containerStyle = {
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingLeftRight}px`,
    paddingRight: `${paddingLeftRight}px`,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    marginLeft: `${marginLeftRight}px`,
    marginRight: `${marginLeftRight}px`,
    borderRadius: `${borderRadius}px`
  } as React.CSSProperties;

  const contentDirection = direction === 'row' ? 'flex-row' : 'flex-col';
  const contentGap = direction === 'row' ? 'gap-4' : 'gap-2';

  // Get the correct color schema from block data
  const colorSchema = block?.color_schema || settings?.color_schema;

  return (
    <div
      className={`minicart__usp ${colorSchema ? `color-${colorSchema}` : ''}`}
      style={containerStyle}
    >
      <div className={`flex ${contentDirection} ${contentGap} items-center justify-between`}>
        {settings?.money_back_text && (
          <div className='flex items-center'>
            <div
              className={moneyBackTextStyle}
              style={{ color: moneyBackTextColor }}
            >
              {settings.money_back_text}
            </div>
          </div>
        )}
        {settings?.free_shipping_text && (
          <div className='flex items-center'>
            <div
              className={freeShippingTextStyle}
              style={{ color: freeShippingTextColor }}
            >
              {settings.free_shipping_text}
            </div>
          </div>
        )}
      </div>

      {showDivider && (
        <div
          style={{
            width: '100%',
            height: dividerThickness,
            backgroundColor: dividerColor
          }}
        />
      )}
    </div>
  );
};
